/**
 * CWS-1944: pure rules for the tablet's OCR plate-verification discrepancy block. No Angular
 * imports — the component computes state via this util and only binds the result.
 */

/** `ocr_verification_status` values that hard-block order creation (spec C3). */
export const OCR_BLOCKING_STATUSES: ReadonlySet<string> = new Set([
  'discrepancy',
  'membership_candidate',
  'failed',
]);

export type OcrConfirmPlateChoice = 'camera' | 'ocr' | 'manual';
export type OcrDiscrepancyOptionKind = 'camera' | 'ocr' | 'third' | 'manual';

export interface OcrDiscrepancyOption {
  kind: OcrDiscrepancyOptionKind;
  plate: string | null;
  state: string | null;
}

/** Backend shape (Task 6/8): `count` always present; name/package only when `count === 1`. */
export interface OcrMembershipHint {
  count: number;
  customer_name?: string;
  package_name?: string;
}

export interface OcrThirdCandidate {
  plate: string;
  state?: string | null;
}

/** The entry fields this util reads — a subset of the LPR entry row / push payload. */
export interface OcrDiscrepancyEntry {
  plate?: string | null;
  state?: string | null;
  ocr_alternate_plate?: string | null;
  ocr_alternate_state?: string | null;
  ocr_verification_status?: string | null;
  ocr_membership_hint?: OcrMembershipHint | null;
  ocr_third_candidate?: OcrThirdCandidate | null;
  ocr_auto_correction?: OcrAutoCorrection | null;
}

/** CWS-1944 auto-fill: the system applied a high-confidence correction without an attendant tap. */
export interface OcrAutoCorrection {
  from: string;
  to: string;
  provider?: string | null;
}

export interface OcrDiscrepancyState {
  blocked: boolean;
  failedMode: boolean;
  options: OcrDiscrepancyOption[];
  hint: OcrMembershipHint | null;
  /** Non-null when the plate was auto-corrected (status `auto_corrected`) — never blocks; shows a notice. */
  autoCorrection: OcrAutoCorrection | null;
}

/**
 * Blocking rule (C3): `discrepancy` / `membership_candidate` / `failed` block; everything else,
 * including null and every `skipped_*`, never blocks. `failedMode` is true only for `failed` —
 * verification could not run at all, so there is no stored alternate to offer.
 */
export function ocrDiscrepancyState(
  entry: OcrDiscrepancyEntry | null | undefined
): OcrDiscrepancyState {
  const status = entry?.ocr_verification_status ?? null;
  const blocked = status != null && OCR_BLOCKING_STATUSES.has(status);
  const failedMode = status === 'failed';

  const options: OcrDiscrepancyOption[] = [];
  if (blocked) {
    options.push({
      kind: 'camera',
      plate: entry?.plate ?? null,
      state: entry?.state ?? null,
    });
    if (entry?.ocr_alternate_plate) {
      options.push({
        kind: 'ocr',
        plate: entry.ocr_alternate_plate,
        state: entry.ocr_alternate_state ?? null,
      });
    }
    if (entry?.ocr_third_candidate?.plate) {
      options.push({
        kind: 'third',
        plate: entry.ocr_third_candidate.plate,
        state: entry.ocr_third_candidate.state ?? null,
      });
    }
    options.push({ kind: 'manual', plate: null, state: null });
  }

  return {
    blocked,
    failedMode,
    options,
    hint: entry?.ocr_membership_hint ?? null,
    autoCorrection:
      status === 'auto_corrected' && entry?.ocr_auto_correction?.from
        ? entry.ocr_auto_correction
        : null,
  };
}

/**
 * The discrepancy banner's headline, or `''` when nothing is blocked (nothing to say). `failedMode`
 * gets the "verification unavailable" copy (C3); every other blocking status gets the plain
 * discrepancy copy.
 */
export function ocrDiscrepancyMessage(
  state: Pick<OcrDiscrepancyState, 'blocked' | 'failedMode'>
): string {
  if (!state.blocked) {
    return '';
  }
  return state.failedMode
    ? 'Plate verification unavailable. Confirm the camera plate or enter one manually.'
    : 'This plate could not be verified. Confirm the correct plate to continue.';
}

/**
 * The membership-hint line: named when the backend sent exactly one candidate
 * (`customer_name`/`package_name` present), count-only for two or more, `null` when there is no
 * hint at all. The backend never sends a name alongside a count (Task 6/8 contract) — this just
 * renders whichever shape arrived.
 */
export function ocrMembershipHintMessage(
  hint: OcrMembershipHint | null | undefined
): string | null {
  if (!hint) {
    return null;
  }
  if (!hint.customer_name) {
    return `Possible match: ${hint.count} customers found`;
  }
  return hint.package_name
    ? `Possible match: ${hint.customer_name} — ${hint.package_name}`
    : `Possible match: ${hint.customer_name}`;
}

/**
 * The confirm-plate `choice` a rendered option submits. The backend endpoint only knows
 * `camera`/`ocr`/`manual` (C4) — `lpr_entries` stores just the camera plate and ONE alternate, so
 * there is no server-side "third" slot. Picking the third candidate is therefore submitted as a
 * `manual` override carrying that candidate's own plate/state, not a distinct server choice.
 */
export function ocrConfirmChoiceForOption(kind: OcrDiscrepancyOptionKind): OcrConfirmPlateChoice {
  return kind === 'camera' || kind === 'ocr' ? kind : 'manual';
}

export interface ConfirmPlateRequest {
  choice: OcrConfirmPlateChoice;
  plate?: string;
  state?: string;
}

/** Stored-plate shape, same floor the backend validates against (`^[A-Z0-9]{2,8}$`). */
const MANUAL_PLATE_PATTERN = /^[A-Z0-9]{2,8}$/;

/**
 * `camera`/`ocr` requests carry no plate — the backend derives them from the locked row. `manual`
 * requires a non-empty, valid plate after uppercase/trim; an invalid one yields `null` so the
 * caller never POSTs it.
 */
export function buildConfirmPlateRequest(
  choice: OcrConfirmPlateChoice,
  manualPlate?: string | null,
  manualState?: string | null
): ConfirmPlateRequest | null {
  if (choice === 'camera' || choice === 'ocr') {
    return { choice };
  }
  if (choice === 'manual') {
    const plate = (manualPlate ?? '').trim().toUpperCase();
    if (!MANUAL_PLATE_PATTERN.test(plate)) {
      return null;
    }
    const state = (manualState ?? '').trim().toUpperCase();
    return { choice: 'manual', plate, ...(state ? { state } : {}) };
  }
  return null;
}

/** `error.data.code` on a 409 from POST /order/checkout or order creation (C3/C5). */
export function isLprPlateUnconfirmedError(error: unknown): boolean {
  const body = (error as { error?: Record<string, any> } | null)?.error;
  const code = body?.['data']?.['code'] ?? body?.['code'];
  return code === 'LPR_PLATE_UNCONFIRMED';
}
